import React, { useState } from 'react';
import { useQuery, useMutation, useQueryClient } from 'react-query';
import { toast } from 'react-toastify';
import api from '../services/api';
import { useAuth } from '../contexts/AuthContext';
import { atPlanLimit, planFeatures } from '../utils/planFeatures';
import Modal from '../components/Modal';
import ConfirmDialog from '../components/ConfirmDialog';
import { handlePhoneInput, cleanPhoneNumber } from '../utils/phoneValidation';
import { getCountryOptions, getCitiesByCountry } from '../utils/countryCityData';
import NationalitySelect from '../components/NationalitySelect';
import './Locations.css';

const Locations = () => {
  const { user } = useAuth();
  const [showModal, setShowModal] = useState(false);
  const [editingLocation, setEditingLocation] = useState(null);
  const [confirmDialog, setConfirmDialog] = useState(null);
  const [formData, setFormData] = useState({
    name: '',
    address: '',
    phone: '',
    country: 'Perú',
    city: '',
  });

  const queryClient = useQueryClient();

  // Obtener lista de sedes
  const { data: locations, isLoading } = useQuery(
    'locations',
    async () => {
      const response = await api.get('/locations');
      return response.data;
    }
  );

  // Mutación para crear sede
  const createMutation = useMutation(
    async (data) => {
      const response = await api.post('/locations', data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Sede creada exitosamente');
        queryClient.invalidateQueries('locations');
        handleCloseModal();
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Error al crear sede');
      }
    }
  );

  // Mutación para actualizar sede
  const updateMutation = useMutation(
    async ({ id, data }) => {
      const response = await api.put(`/locations/${id}`, data);
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Sede actualizada exitosamente');
        queryClient.invalidateQueries('locations');
        handleCloseModal();
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Error al actualizar sede');
      }
    }
  );

  // Mutación para desactivar/activar sede
  const toggleActiveMutation = useMutation(
    async ({ id, isActive }) => {
      const response = await api.put(`/locations/${id}`, { is_active: !isActive });
      return response.data;
    },
    {
      onSuccess: () => {
        toast.success('Estado de sede actualizado');
        queryClient.invalidateQueries('locations');
      },
      onError: (error) => {
        toast.error(error.response?.data?.detail || 'Error al actualizar estado');
      }
    }
  );

  const handleOpenModal = (location = null) => {
    if (location) {
      setEditingLocation(location);
      setFormData({
        name: location.name || '',
        address: location.address || '',
        phone: location.phone || '',
        country: location.country || 'Perú',
        city: location.city || '',
      });
    } else {
      setEditingLocation(null);
      setFormData({
        name: '',
        address: '',
        phone: '',
        country: 'Perú',
        city: '',
      });
    }
    setShowModal(true);
  };

  const handleCloseModal = () => {
    setShowModal(false);
    setEditingLocation(null);
    setFormData({
      name: '',
      address: '',
      phone: '',
      country: 'Perú',
      city: '',
    });
  };

  const handleSubmit = (e) => {
    e.preventDefault();
    
    // Validar que haya un nombre
    if (!formData.name || formData.name.trim() === '') {
      toast.error('El nombre de la sede es obligatorio');
      return;
    }
    
    // Preparar datos para enviar (solo incluir campos con valor)
    const dataToSend = {};
    if (formData.name.trim()) dataToSend.name = formData.name.trim();
    if (formData.address.trim()) dataToSend.address = formData.address.trim();
    if (formData.phone.trim()) dataToSend.phone = cleanPhoneNumber(formData.phone.trim());
    if (formData.city.trim()) dataToSend.city = formData.city.trim();
    
    if (editingLocation) {
      updateMutation.mutate({ id: editingLocation.id, data: dataToSend });
    } else {
      createMutation.mutate(dataToSend);
    }
  };

  const handleToggleActive = (location) => {
    const action = location.is_active ? 'desactivar' : 'activar';
    setConfirmDialog({
      isOpen: true,
      title: `${action.charAt(0).toUpperCase() + action.slice(1)} Sede`,
      message: `¿Estás seguro de ${action} esta sede?`,
      onConfirm: () => {
        toggleActiveMutation.mutate({ id: location.id, isActive: location.is_active });
        setConfirmDialog(null);
      },
      onCancel: () => setConfirmDialog(null),
      confirmText: action.charAt(0).toUpperCase() + action.slice(1),
      cancelText: 'Cancelar',
    });
  };

  const maxLocations = planFeatures(user).max_locations;
  const activeLocations = (locations || []).filter((item) => item.is_active !== false).length;
  const locationLimitReached = atPlanLimit(activeLocations, maxLocations);

  if (isLoading) {
    return <div className="loading">Cargando sedes...</div>;
  }

  return (
    <div className="page-container">
      <div className="page-header">
        <h2>Gestión de Sedes</h2>
        <button 
          className="btn btn-primary" 
          onClick={() => handleOpenModal()}
          disabled={locationLimitReached}
          title={locationLimitReached ? 'El plan Free permite 1 sede' : undefined}
        >
          + Nueva Sede
        </button>
      </div>
      {locationLimitReached && (
        <p className="plan-hint">El plan Free permite hasta 1 sede.</p>
      )}

      <div className="card">
        {locations && locations.length > 0 ? (
          <div className="locations-grid">
            {locations.map((location) => (
              <div 
                key={location.id} 
                className={`location-card ${!location.is_active ? 'inactive' : ''}`}
              >
                <div className="location-header">
                  <div className="location-icon">📍</div>
                  <div className="location-info">
                    <h3>{location.name}</h3>
                    <span className={`badge ${location.is_active ? 'badge-success' : 'badge-danger'}`}>
                      {location.is_active ? 'Activa' : 'Inactiva'}
                    </span>
                  </div>
                </div>
                <div className="location-details">
                  {location.code && (
                    <p><strong>Código:</strong> {location.code}</p>
                  )}
                  {location.address && (
                    <p><strong>Dirección:</strong> {location.address}</p>
                  )}
                  {location.city && (
                    <p><strong>Ciudad:</strong> {location.city}</p>
                  )}
                  {location.phone && (
                    <p><strong>Teléfono:</strong> {location.phone}</p>
                  )}
                </div>
                <div className="location-actions">
                  <button
                    className="btn btn-secondary btn-sm"
                    onClick={() => handleOpenModal(location)}
                  >
                    Editar
                  </button>
                  <button
                    className={`btn btn-sm ${location.is_active ? 'btn-warning' : 'btn-success'}`}
                    onClick={() => handleToggleActive(location)}
                    disabled={toggleActiveMutation.isLoading}
                  >
                    {location.is_active ? 'Desactivar' : 'Activar'}
                  </button>
                </div>
              </div>
            ))}
          </div>
        ) : (
          <div className="empty-state">
            <p>No hay sedes registradas</p>
            <button 
              className="btn btn-primary" 
              onClick={() => handleOpenModal()}
            >
              Crear primera sede
            </button>
          </div>
        )}
      </div>

      {/* Modal para crear/editar */}
      <Modal
        isOpen={showModal}
        onClose={handleCloseModal}
        title={editingLocation ? 'Editar Sede' : 'Nueva Sede'}
        closeOnOverlayClick={false}
      >
        <form onSubmit={handleSubmit} className="modal-form">
              <div className="form-group">
                <label>Nombre de la Sede *</label>
                <input
                  type="text"
                  value={formData.name}
                  onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                  placeholder="Ej: Sede Central, Sede Norte, etc."
                  required
                />
              </div>

              <div className="form-group">
                <label>Dirección</label>
                <input
                  type="text"
                  value={formData.address}
                  onChange={(e) => setFormData({ ...formData, address: e.target.value })}
                  placeholder="Dirección completa"
                />
              </div>

              <div className="form-group">
                <label>País</label>
                <NationalitySelect
                  value={formData.country}
                  allowEmpty
                  emptyLabel="Seleccionar país..."
                  options={getCountryOptions().map((opt) => ({
                    code: opt.code,
                    name: opt.label,
                    value: opt.value,
                  }))}
                  onChange={(country) => setFormData({ ...formData, country, city: '' })}
                />
              </div>

              <div className="form-group">
                <label>Ciudad</label>
                <select
                  value={formData.city}
                  onChange={(e) => setFormData({ ...formData, city: e.target.value })}
                  disabled={!formData.country}
                >
                  <option value="">{formData.country ? 'Seleccionar ciudad...' : 'Seleccione un país primero'}</option>
                  {getCitiesByCountry(formData.country).map((city) => (
                    <option key={city} value={city}>{city}</option>
                  ))}
                </select>
              </div>

              <div className="form-group">
                <label>Teléfono</label>
                <input
                  type="tel"
                  value={formData.phone}
                  onChange={(e) => {
                    handlePhoneInput(e);
                    setFormData({ ...formData, phone: e.target.value });
                  }}
                  placeholder="+51 900 000 000"
                />
              </div>

              <div className="modal-actions">
                <button
                  type="button"
                  className="btn btn-secondary"
                  onClick={handleCloseModal}
                >
                  Cancelar
                </button>
                <button
                  type="submit"
                  className="btn btn-primary"
                  disabled={createMutation.isLoading || updateMutation.isLoading}
                >
                  {createMutation.isLoading || updateMutation.isLoading
                    ? 'Guardando...'
                    : editingLocation
                    ? 'Actualizar'
                    : 'Crear'}
                </button>
              </div>
            </form>
      </Modal>

      {confirmDialog && (
        <ConfirmDialog
          isOpen={confirmDialog.isOpen}
          title={confirmDialog.title}
          message={confirmDialog.message}
          onConfirm={confirmDialog.onConfirm}
          onClose={confirmDialog.onCancel}
          confirmText={confirmDialog.confirmText}
          cancelText={confirmDialog.cancelText}
        />
      )}
    </div>
  );
};

export default Locations;

